import React, {
  AnchorHTMLAttributes,
  HTMLAttributeAnchorTarget,
  ReactNode,
  useContext,
} from "react";
import FlexBox from "../../Components/FlexBox/FlexBox";
import Icon from "../../Foundation/Icon/Icon";
import Typography, {
  ITypographyProps,
} from "../../Foundation/Typography/Typography";
import { IconName } from "../../lib/svgIcon/components/index";
import { useTheme } from "../../Themes/defaultTheme";
import LinkContext from "./Context/LinkContext";
import { GatsbyLinkProps } from "gatsby-link";
import { TypographyLinkProps } from "./Context/DefaultTypographyLink";
import { FlexBoxLinkProps } from "./Context/DefaultFlexBoxLink";
import { IIconProps } from "../../Interface/IconProps";

export interface ILink {
  label: ReactNode;
  url?: string;
  icon?: IconName | ((color: string) => ReactNode);
  iconProps?: IIconProps;
  gap?: number;
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  target?: HTMLAttributeAnchorTarget;
}

export type ILinkProps = Omit<ITypographyProps, "children"> &
  ILink & {
    anchorProps?: AnchorHTMLAttributes<HTMLAnchorElement>;
    children?: ReactNode;
    beforeClick?: () => Promise<void>;
  };
export interface RollingStartLinkProps<TState> extends GatsbyLinkProps<TState> {
  href?: string;
}
export type TRollingStartLink<TState = any> = (
  props: RollingStartLinkProps<TState> & {
    beforeClick?: ILinkProps["beforeClick"];
  }
) => JSX.Element;

export type TRollingStartStyledLink<TState = any> = (
  props: RollingStartLinkProps<TState> & {
    $hover: boolean;
    $color: string;
    beforeClick?: ILinkProps["beforeClick"];
  }
) => JSX.Element;

export type TRollingStartTypographyLink<
  TState = any,
  Typo extends any = any
> = (
  props: RollingStartLinkProps<TState> & TypographyLinkProps<Typo>
) => JSX.Element;
export type TRollingStartFlexBoxLink<TState = any, Typo extends any = any> = (
  props: RollingStartLinkProps<TState> & FlexBoxLinkProps<Typo>
) => JSX.Element;

const Link = (props: ILinkProps) => {
  const {
    label,
    url,
    className,
    icon,
    gap = 12,
    target,
    iconProps,
    anchorProps,
    children,
    beforeClick,
    ...typographyProps
  } = props;
  const { variant = "body", color = "white", ...rest } = typographyProps;
  const theme = useTheme();
  const contentColor =
    typeof color === "function" ? color() : theme.color[color];
  const iconRender = icon ? (
    typeof icon === "function" ? (
      icon(contentColor)
    ) : (
      <Icon icon={icon} color={contentColor} {...iconProps} />
    )
  ) : null;
  const { StyledLink } = useContext(LinkContext);

  const typographyRender =
    children ||
    (label ? (
      <Typography
        as={"span"}
        className="link-text"
        variant={variant}
        color={() => contentColor}
        {...rest}
      >
        {label}
      </Typography>
    ) : null);

  const textRender = iconRender ? (
    <FlexBox
      as={"span"}
      gap={gap}
      align="center"
      style={{ display: "inline-flex" }}
    >
      {iconRender}
      {typographyRender}
    </FlexBox>
  ) : (
    typographyRender
  );
  return (
    <StyledLink
      target={target}
      to={url || ""}
      className={className}
      $hover={false}
      $color={contentColor}
      {...anchorProps}
      beforeClick={beforeClick}
    >
      {textRender}
    </StyledLink>
  );
};

export default Link;
